<template>
  <div id="tunnelMigration">

    <!-- Toolbar -->
    <div class="card toolbar">
      <div class="field">
        <span class="label">Sales period</span>
        <div class="segmented">
          <button v-for="d of [30, 60, 90, 180]" :key="`d_${d}`" :class="{ on: days === d }" @click="setDays(d)">{{ d }} d</button>
        </div>
      </div>
      <div class="field">
        <span class="label">POS</span>
        <div class="segmented">
          <button :class="{ on: vendorFilter === '' }" @click="vendorFilter = ''">All</button>
          <button :class="{ on: vendorFilter === 'veloce' }" @click="vendorFilter = 'veloce'">Veloce</button>
          <button :class="{ on: vendorFilter === 'maitre' }" @click="vendorFilter = 'maitre'">Maitre'D</button>
        </div>
      </div>
      <div class="field grow">
        <span class="label">Search</span>
        <input class="search" type="text" v-model="search" placeholder="Company, organization, host…">
      </div>
      <label class="toggle">
        <input id="tm_withoutSales" type="checkbox" v-model="withoutSales" @change="load">
        <span class="track"><span class="knob"></span></span>
        <span>Include sites without sales</span>
      </label>
      <button class="btn btn-primary refresh" @click="load" :disabled="loading">
        <span v-if="loading">Loading…</span><span v-else>Refresh</span>
      </button>
    </div>

    <!-- Progress + status cards -->
    <div v-if="summary" class="overview">
      <div class="card progress">
        <div class="progress-head">
          <div>
            <div class="eyebrow">Migration progress</div>
            <div class="big">{{ progress.migrated }} <span class="of">of {{ progress.total }} sites migrated</span></div>
          </div>
          <div class="pct">{{ progress.pct }}%</div>
        </div>
        <div class="bar">
          <span class="seg migrated" :style="{ width: pctOf(summary.migrated) }"></span>
          <span class="seg polling_cleanup" :style="{ width: pctOf(summary.polling_cleanup) }"></span>
          <span class="seg direct" :style="{ width: pctOf(summary.direct) }"></span>
          <span class="seg tunnel" :style="{ width: pctOf(summary.tunnel) }"></span>
        </div>
        <div class="progress-foot">
          <span>{{ summary.salesInPeriod }} sales in {{ days }} days</span>
          <span :class="{ bad: summary.posFailedInPeriod > 0 }">{{ summary.posFailedInPeriod }} POS failed</span>
          <span class="muted">Status is read from the live POS configuration. Nothing is stored: a site turns "migrated" as soon as its tunnel settings are gone.</span>
        </div>
      </div>

      <div class="stats">
        <button v-for="s of statuses" :key="`st_${s.key}`" class="card stat" :class="[s.key, { on: statusFilter === s.key }]" @click="toggleStatus(s.key)">
          <span class="dot"></span>
          <span class="count">{{ summary[s.key] || 0 }}</span>
          <span class="name">{{ s.label }}</span>
          <span class="desc">{{ s.desc }}</span>
        </button>
      </div>
    </div>

    <div v-if="unavailable" class="card empty">
      <div class="empty-title">Not available on this server yet</div>
      <div class="muted">The API of the selected server does not expose the tunnel migration data (deployed on NA0 first).</div>
    </div>

    <!-- Table -->
    <div class="card table-card" v-if="rows">
      <div class="table-head">
        <div class="title">Sites <span class="muted">{{ filteredRows.length }}<span v-if="filteredRows.length !== rows.length"> of {{ rows.length }}</span></span></div>
        <div class="legend">
          <span><i class="dot ok"></i> agent seen &lt; 5 min</span>
          <span><i class="dot warn"></i> &lt; 1 h</span>
          <span><i class="dot bad"></i> older / never</span>
        </div>
      </div>

      <div v-if="!filteredRows.length" class="empty">
        <div class="empty-title">No site matches</div>
        <div class="muted">Try a longer period, another status, or include sites without sales.</div>
      </div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Site</th>
              <th>POS</th>
              <th>Status</th>
              <th>Endpoints</th>
              <th class="num">Sales</th>
              <th>Agent</th>
              <th>To do</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row of filteredRows" :key="`tr_${row.companyId}`">
              <td class="site">
                <div class="name">{{ row.companyName }}<span v-if="!row.companyActive" class="chip off">inactive</span></div>
                <div class="sub">{{ row.branchName }} · #{{ row.companyId }}</div>
              </td>
              <td>
                <div class="name">{{ vendorLabel(row.vendor) }}</div>
                <div class="sub">{{ row.mode === 'push' ? 'cloud push' : (row.mode === 'polling' ? 'agent polling' : '—') }}<span v-if="row.mode === 'polling' && row.pollingDelay"> · {{ row.pollingDelay }} s / {{ row.pollingMaxRetry }} retries</span></div>
              </td>
              <td><span class="pill" :class="row.status">{{ statusLabel(row.status) }}</span></td>
              <td class="endpoints">
                <div v-if="row.pushEndpoint"><span class="k">cloud</span><code>{{ row.pushEndpoint }}</code></div>
                <div v-if="row.localEndpoint"><span class="k">local</span><code>{{ row.localEndpoint }}</code></div>
                <div v-if="!row.pushEndpoint && !row.localEndpoint" class="muted">—</div>
              </td>
              <td class="num">
                <div class="name">{{ row.salesInPeriod }}</div>
                <div class="sub"><span v-if="row.posFailedInPeriod > 0" class="chip bad">{{ row.posFailedInPeriod }} failed</span><span v-else-if="row.lastSaleAt">last {{ formatDate(row.lastSaleAt) }}</span><span v-else>no sale yet</span></div>
              </td>
              <td class="agent">
                <div class="name">
                  <span class="chip" :class="buildClass(row.agentBuildVersion)">{{ row.agentBuildVersion || 'build unknown' }}</span>
                </div>
                <div class="sub"><i class="dot" :class="activityClass(row.lastReceptionActivityAt)"></i>{{ formatDate(row.lastReceptionActivityAt) }}<span v-if="row.compiledBackendVersion"> · {{ row.compiledBackendVersion }}</span></div>
              </td>
              <td class="todo">
                <span v-if="!row.todo.length && row.status === 'migrated'" class="chip ok">done</span>
                <span v-else-if="!row.todo.length && row.status !== 'other'" class="chip ok">ready</span>
                <span v-else-if="!row.todo.length" class="muted">—</span>
                <span v-for="(t, i) of row.todo" :key="i" class="chip">{{ t }}</span>
              </td>
              <td class="actions"><button class="link" @click="goToExternalPos(row)">Settings</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </div>
</template>

<script>
  import { Core } from '@/core/Core';
  import moment from "moment-timezone";

  const MIN_POLLING_BUILD = [3, 0, 3805]; /* first agent build with Veloce polling on the LAN */

  export default {
    name: "TunnelMigration",

    data() {
      return {
        days: 30,
        withoutSales: false,
        statusFilter: '',
        vendorFilter: '',
        search: '',
        rows: null,
        summary: null,
        loading: false,
        unavailable: false,
        statuses: [
          { key: 'tunnel', label: 'On the tunnel', desc: 'cloud pushes through tunnel.ishopfood.com' },
          { key: 'direct', label: 'Direct push', desc: 'cloud pushes to a public address' },
          { key: 'polling_cleanup', label: 'Polling, cleanup', desc: 'agent polls, tunnel settings remain' },
          { key: 'migrated', label: 'Migrated', desc: 'agent polling, no tunnel left' },
          { key: 'other', label: 'Other', desc: 'tunnel config without a POS' }
        ]
      }
    },

    computed: {
      contextServer() {
        return this.$store.state.contextServer;
      },
      progress() {
        let s = this.summary || {};
        let total = (s.tunnel || 0) + (s.direct || 0) + (s.polling_cleanup || 0) + (s.migrated || 0);
        let migrated = s.migrated || 0;
        return { total, migrated, pct: total ? Math.round(migrated / total * 100) : 0 };
      },
      filteredRows() {
        if (!this.rows) {
          return [];
        }
        let search = (this.search || '').toLowerCase().trim();
        return this.rows.filter(r =>
          (!this.statusFilter || r.status === this.statusFilter)
          && (!this.vendorFilter || r.vendor === this.vendorFilter)
          && (!search || [r.companyName, r.branchName, r.companyCanonical, r.pushEndpoint, r.localEndpoint, String(r.companyId)]
                .some(v => v && String(v).toLowerCase().includes(search)))
        );
      }
    },

    watch: {
      contextServer() {
        this.load();
      }
    },

    mounted() {
      this.$store.state.pageTitleTranslationKey = "advanced_navigation.tunnel_migration";
      this.$store.state.pageIcon = "";
      this.load();
    },

    methods: {
      async load() {
        this.loading = true;
        let response = await Core.noThrowRequest('GET', '/api/monitoring/tunnel-migration', {
          days: this.days,
          withoutSales: this.withoutSales ? 1 : 0
        });
        this.loading = false;
        if (response && Array.isArray(response.rows)) {
          this.rows = response.rows;
          this.summary = response.summary;
          this.unavailable = false;
        } else {
          this.rows = null;
          this.summary = null;
          this.unavailable = true;
        }
      },
      setDays(d) {
        this.days = d;
        this.load();
      },
      toggleStatus(key) {
        this.statusFilter = this.statusFilter === key ? '' : key;
      },
      pctOf(n) {
        return (this.progress.total ? (n || 0) / this.progress.total * 100 : 0) + '%';
      },
      statusLabel(key) {
        let s = this.statuses.find(s => s.key === key);
        return s ? s.label : key;
      },
      vendorLabel(vendor) {
        return vendor === 'maitre' ? "Maitre'D" : (vendor === 'veloce' ? 'Veloce' : '—');
      },
      buildIsRecent(build) {
        let parts = String(build).split('.').map(n => parseInt(n, 10));
        for (let i = 0; i < MIN_POLLING_BUILD.length; i++) {
          if ((parts[i] || 0) > MIN_POLLING_BUILD[i]) return true;
          if ((parts[i] || 0) < MIN_POLLING_BUILD[i]) return false;
        }
        return true;
      },
      buildClass(build) {
        if (!build) return 'off';
        return this.buildIsRecent(build) ? 'ok' : 'warn';
      },
      activityClass(value) {
        if (!value) return 'off';
        let minutes = moment().diff(moment(value), 'minutes');
        return minutes <= 5 ? 'ok' : (minutes <= 60 ? 'warn' : 'bad');
      },
      formatDate(value) {
        if (!value) return '—';
        let m = moment(value);
        return m.isValid() ? m.format('MMM D, HH:mm') : String(value).substring(0, 16);
      },
      goToExternalPos(row) {
        this.$router.push({ path: '/settings/external-pos', query: { branch: row.branchId, company: row.companyId } });
      }
    }
  }
</script>

<style lang="scss" scoped>
  $ink: #1f2430;
  $muted: #6b7280;
  $line: #e6e8ec;
  $surface: #fff;
  $primary: #fc6650;
  $tunnel: #d1352b;
  $direct: #d97a06;
  $cleanup: #b58900;
  $migrated: #1f8a44;
  $other: #8a8f98;

  #tunnelMigration { display: grid; gap: 16px; color: $ink; }

  .card { background: $surface; border: 1px solid $line; border-radius: 10px; box-shadow: 0 1px 2px rgba(16, 24, 40, .04); }
  .muted { color: $muted; }
  .eyebrow { font-size: 11px; letter-spacing: .08em; text-transform: uppercase; color: $muted; font-weight: 600; }

  /* Toolbar */
  .toolbar { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 16px 20px; padding: 14px 18px; }
  .field { display: grid; gap: 6px; }
  .field.grow { flex: 1 1 220px; }
  .label { font-size: 11px; letter-spacing: .06em; text-transform: uppercase; color: $muted; font-weight: 600; }
  .segmented {
    display: inline-flex; border: 1px solid $line; border-radius: 8px; overflow: hidden; background: #f7f8fa;
    button { border: 0; background: transparent; padding: 7px 12px; font-size: 13px; color: $muted; cursor: pointer; font-weight: 500; }
    button + button { border-left: 1px solid $line; }
    button.on { background: $ink; color: #fff; }
  }
  .search { height: 34px; border: 1px solid $line; border-radius: 8px; padding: 0 12px; font-size: 13.5px; width: 100%; background: #fff; }
  .search:focus { outline: none; border-color: $ink; }
  .toggle {
    display: flex; align-items: center; gap: 8px; font-size: 13px; color: $ink; cursor: pointer; margin-bottom: 7px;
    input { display: none; }
    .track { width: 34px; height: 20px; border-radius: 10px; background: #d7dbe2; position: relative; transition: background .15s; }
    .knob { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; transition: left .15s; box-shadow: 0 1px 2px rgba(0,0,0,.2); }
    input:checked + .track { background: $migrated; }
    input:checked + .track .knob { left: 16px; }
  }
  .refresh { height: 34px; margin-bottom: 1px; }

  /* Overview */
  .overview { display: grid; grid-template-columns: minmax(280px, 1.2fr) 2fr; gap: 16px; }
  @media (max-width: 1100px) { .overview { grid-template-columns: 1fr; } }
  .progress { padding: 16px 18px; display: grid; gap: 12px; align-content: start; }
  .progress-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
  .big { font-size: 24px; font-weight: 700; line-height: 1.2; margin-top: 4px; .of { font-size: 13px; font-weight: 500; color: $muted; } }
  .pct { font-size: 34px; font-weight: 700; color: $migrated; line-height: 1; }
  .bar { display: flex; height: 10px; border-radius: 6px; overflow: hidden; background: #eef0f3; }
  .seg { display: block; height: 100%; }
  .seg.migrated { background: $migrated; } .seg.polling_cleanup { background: $cleanup; } .seg.direct { background: $direct; } .seg.tunnel { background: $tunnel; }
  .progress-foot { display: flex; flex-wrap: wrap; gap: 6px 16px; font-size: 12.5px; .bad { color: $tunnel; font-weight: 600; } .muted { flex-basis: 100%; } }

  .stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; }
  .stat {
    text-align: left; padding: 12px 14px; display: grid; gap: 2px; cursor: pointer; font: inherit; color: $ink; position: relative;
    transition: border-color .15s, transform .15s;
    .dot { position: absolute; top: 14px; right: 14px; width: 10px; height: 10px; border-radius: 50%; }
    .count { font-size: 26px; font-weight: 700; line-height: 1.1; }
    .name { font-size: 13px; font-weight: 600; }
    .desc { font-size: 11.5px; color: $muted; }
    &:hover { transform: translateY(-1px); }
    &.on { border-color: $ink; box-shadow: 0 0 0 2px rgba(31, 36, 48, .12); }
    &.tunnel .dot { background: $tunnel; } &.direct .dot { background: $direct; } &.polling_cleanup .dot { background: $cleanup; }
    &.migrated .dot { background: $migrated; } &.other .dot { background: $other; }
  }

  /* Table */
  .table-card { overflow: hidden; }
  .table-head { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid $line; flex-wrap: wrap; gap: 8px; }
  .title { font-weight: 600; font-size: 15px; .muted { font-weight: 500; margin-left: 6px; } }
  .legend { display: flex; gap: 14px; font-size: 12px; color: $muted; span { display: flex; align-items: center; gap: 5px; } }
  .table-wrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  thead th { position: sticky; top: 0; background: #f7f8fa; text-align: left; font-size: 11px; letter-spacing: .06em; text-transform: uppercase; color: $muted; font-weight: 600; padding: 10px 14px; border-bottom: 1px solid $line; white-space: nowrap; }
  tbody td { padding: 12px 14px; border-bottom: 1px solid $line; vertical-align: top; }
  tbody tr:last-child td { border-bottom: 0; }
  tbody tr:hover td { background: #fafbfc; }
  .name { font-weight: 600; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .sub { font-size: 12px; color: $muted; margin-top: 2px; display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
  td.num, th.num { text-align: right; .name { justify-content: flex-end; } .sub { justify-content: flex-end; } }
  .endpoints { font-size: 12px; display: grid; gap: 3px;
    .k { display: inline-block; width: 38px; color: $muted; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
    code { font-family: Consolas, Monaco, monospace; font-size: 12px; background: #f3f4f6; padding: 1px 6px; border-radius: 4px; }
  }
  .todo { display: flex; flex-wrap: wrap; gap: 4px; max-width: 300px; .chip { white-space: normal; } }
  .chip { display: inline-block; font-size: 11.5px; font-weight: 500; padding: 2px 8px; border-radius: 999px; background: #f1f3f5; color: $ink; white-space: nowrap; }
  .chip.ok { background: #e3f3e8; color: $migrated; } .chip.warn { background: #fdf0d5; color: $direct; } .chip.bad { background: #fbe4e2; color: $tunnel; } .chip.off { background: #f1f3f5; color: $muted; }
  .pill { display: inline-block; font-size: 11.5px; font-weight: 600; padding: 3px 10px; border-radius: 999px; white-space: nowrap; }
  .pill.tunnel { background: #fbe4e2; color: $tunnel; } .pill.direct { background: #fdf0d5; color: $direct; } .pill.polling_cleanup { background: #fbf3d0; color: #7a5c00; }
  .pill.migrated { background: #e3f3e8; color: $migrated; } .pill.other { background: #f1f3f5; color: $muted; }
  .dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #c9ced6; }
  .dot.ok { background: $migrated; } .dot.warn { background: $direct; } .dot.bad { background: $tunnel; } .dot.off { background: #c9ced6; }
  .actions { text-align: right; white-space: nowrap; }
  .link { border: 1px solid $line; background: #fff; border-radius: 6px; padding: 5px 10px; font-size: 12.5px; cursor: pointer; color: $ink; }
  .link:hover { border-color: $ink; }
  .empty { padding: 48px 18px; text-align: center; .empty-title { font-weight: 600; font-size: 15px; margin-bottom: 4px; } }
</style>
